<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import moment from 'moment'
import { copyText, handleTreeData } from '@/common/js/common.js'
import area from '@/common/json/area.json'

const store = useStore()

const activeTab = ref('baseInfo')
const formRef = ref()
const rawUserInfo = ref({})
const rawEnterpriseInfo = ref({})
const inviteDisplay = ref({})
const brandOptions = ref([])
const areaInfo = handleTreeData(area, 'name', 'name')
const deptTreeData = [
  {
    id: 1,
    label: '公司部门',
    isRoot: true,
    children: [
      { id: 11, label: '管理部' },
      { id: 12, label: '销售部' },
      { id: 13, label: '商务部' },
      { id: 14, label: '财务部' },
      { id: 15, label: '采购部' },
      { id: 16, label: '仓储部' },
      { id: 17, label: '物流部' }
    ]
  }
]

const createEmptyFormData = () => ({
  name: '',
  panLeiId: '',
  userType: 'supplier',
  phone: '',
  enterpriseName: '',
  enterpriseId: '',
  birthMonth: '',
  gender: '',
  province: '',
  city: '',
  district: '',
  addressArea: [],
  address: '',
  job: '',
  brand: [],
  experience: ''
})

const createEmptyInviterInfo = () => ({
  name: '',
  phone: '',
  id: '',
  enterprise: '',
  settleTime: ''
})

const createEmptyStats = () => ({
  supplier: '',
  customer: '',
  partner: ''
})

const formData = ref(createEmptyFormData())
const initialFormData = ref(createEmptyFormData())
const inviterInfo = ref(createEmptyInviterInfo())
const stats = ref(createEmptyStats())

const rules = {
  name: [{ required: true, message: '请输入姓名', trigger: 'blur' }]
}

const cloneData = value => JSON.parse(JSON.stringify(value))

const displayValue = value => {
  if (value === null || value === undefined || value === '') {
    return '--'
  }
  return value
}

const buildSingleOption = value => {
  if (!value) {
    return []
  }
  return [{ label: value, value }]
}

const jobOptions = (deptTreeData[0]?.children || []).map(item => ({
  key: item.id,
  label: item.label,
  value: item.label
}))

const experienceOptions = Array.from({ length: 50 }, (_, index) => {
  const value = `${index + 1}年`
  return {
    key: index + 1,
    label: value,
    value
  }
})

const selectOptions = computed(() => ({
  job: jobOptions,
  brand: brandOptions.value,
  experience: experienceOptions
}))

const mapUserType = value => {
  if (value === '0' || value === 0 || value === 'supplier') {
    return 'supplier'
  }
  if (value === '1' || value === 1 || value === 'customer') {
    return 'customer'
  }
  if (value === 'factory') {
    return 'factory'
  }
  return 'supplier'
}

const parseMemberOrder = value => {
  if (!value) {
    return ''
  }
  const matched = String(value).trim().match(/(\d+)$/)
  if (!matched) {
    return ''
  }
  return String(Number(matched[1]))
}

const parseDateValue = value => {
  if (!value) {
    return null
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const parsedDate = new Date(String(value).replace(/-/g, '/'))
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const getSettleSourceTime = data =>
  data.createTime ||
  data.createDate ||
  data.registerTime ||
  data.gmtCreate ||
  data.entryTime ||
  data.joinTime ||
  ''

const settleDuration = computed(() => {
  const settleDate = parseDateValue(getSettleSourceTime(rawUserInfo.value))
  if (!settleDate) {
    return { years: 0, months: 0 }
  }
  const now = new Date()
  let years = now.getFullYear() - settleDate.getFullYear()
  let months = now.getMonth() - settleDate.getMonth()
  if (now.getDate() < settleDate.getDate()) {
    months -= 1
  }
  if (months < 0) {
    years -= 1
    months += 12
  }
  return {
    years: Math.max(years, 0),
    months: Math.max(months, 0)
  }
})

const settleYears = computed(() => settleDuration.value.years)
const settleMonths = computed(() => settleDuration.value.months)
const welcomeName = computed(() => displayValue(formData.value.name))
const memberOrder = computed(() => displayValue(parseMemberOrder(formData.value.panLeiId)))
const devLevelText = computed(() => {
  const level = rawUserInfo.value.devLevel
  if (level === null || level === undefined || level === '') {
    return '推广者0级'
  }
  return `推广者${level}级`
})

const currentUserId = computed(() => {
  const localUserInfo = localStorage.getItem('userInfo')
  if (localUserInfo) {
    try {
      const parsedUserInfo = JSON.parse(localUserInfo)
      if (parsedUserInfo?.userId) {
        return parsedUserInfo.userId
      }
    } catch (error) {}
  }
  return rawUserInfo.value.userId || rawUserInfo.value.id || ''
})

const supplierLink = computed(() => {
  if (inviteDisplay.value.INVITE_SUPPLIER_LINK_ADMIN !== '1' || !currentUserId.value) {
    return ''
  }
  return `${window.location.protocol}//${window.location.host}/login/index?uid=${currentUserId.value}&belongType=0`
})

const customerLink = computed(() => {
  if (inviteDisplay.value.INVITE_CUSTOMER_LINK_ADMIN !== '1' || !currentUserId.value) {
    return ''
  }
  return `${window.location.protocol}//${window.location.host}/login/index?uid=${currentUserId.value}&belongType=1`
})

const loadInviteDisplay = async () => {
  const userId = currentUserId.value
  if (!userId) {
    inviteDisplay.value = {}
    return
  }
  const [customerRes, supplierRes] = await Promise.allSettled([
    store.dispatch('systemModule/getDisplayTabListOfHome', {
      setTpCd: 'INVITE_CUSTOMER_LINK_ADMIN',
      sellerId: userId
    }),
    store.dispatch('systemModule/getDisplayTabListOfHome', {
      setTpCd: 'INVITE_SUPPLIER_LINK_ADMIN',
      sellerId: userId
    })
  ])

  inviteDisplay.value = {
    INVITE_CUSTOMER_LINK_ADMIN:
      customerRes.status === 'fulfilled' ? customerRes.value?.data?.data?.value || '' : '',
    INVITE_SUPPLIER_LINK_ADMIN:
      supplierRes.status === 'fulfilled' ? supplierRes.value?.data?.data?.value || '' : ''
  }
}

const loadEnterpriseInfo = async enterpriseId => {
  if (!enterpriseId) {
    rawEnterpriseInfo.value = {}
    return
  }
  try {
    const res = await store.dispatch('myMessageModule/getEnterprise', enterpriseId)
    const { code, data = {} } = res?.data || {}
    rawEnterpriseInfo.value = code === '0' ? data : {}
  } catch (error) {
    rawEnterpriseInfo.value = {}
  }
}

const loadBrandOptions = async () => {
  try {
    const res = await store.dispatch('myMessageModule/listSkuCategoryForFirst', {
      pageNo: 1,
      pageSize: 9999
    })
    const { code, data = {} } = res?.data || {}
    if (code === '0') {
      const list = data.data || data.rows || []
      brandOptions.value = Array.isArray(list)
        ? list.map(item => ({
            key: item.id,
            label: item.categoryName,
            value: item.categoryName
          }))
        : []
    } else {
      brandOptions.value = []
    }
  } catch (error) {
    brandOptions.value = []
  }
}

const fillPageData = userData => {
  const brandValue = userData.brandName || userData.brand || ''
  formData.value = {
    ...createEmptyFormData(),
    name: userData.realName || '',
    panLeiId: userData.customerNumber || userData.userNumber || '',
    userType: mapUserType(userData.userType),
    phone: userData.mobilePhone || '',
    enterpriseName:
      userData.enterpriseName ||
      userData.enterpriseSimpleName ||
      rawEnterpriseInfo.value.enterpriseName ||
      '',
    enterpriseId:
      rawEnterpriseInfo.value.enterpriseCode ||
      userData.enterpriseCode ||
      userData.enterpriseNo ||
      userData.enterpriseId ||
      '',
    birthMonth: userData.birthMonth || '',
    gender: userData.gender || '',
    province: userData.province || '',
    city: userData.city || '',
    district: userData.district || '',
    addressArea: [userData.province, userData.city, userData.district].filter(Boolean),
    address: userData.address || '',
    job: userData.jobPost || '',
    brand: brandValue ? String(brandValue).split(',').map(item => item.trim()).filter(Boolean) : [],
    experience: userData.yearOfWork || ''
  }

  initialFormData.value = cloneData(formData.value)

  inviterInfo.value = {
    ...createEmptyInviterInfo(),
    name: userData.inviteUserName || '',
    phone: userData.inviteUserPhone || '',
    id: userData.inviteUseCustomerNumber || '',
    enterprise: userData.inviteUserEnterpriseName || '',
    settleTime: userData.inviteUserCreateTime || ''
  }

  stats.value = {
    supplier: userData.inviteUserGysCount || '',
    customer: userData.inviteUserKhCount || '',
    partner: userData.inviteUserKhCount || ''
  }
}

const initPage = async () => {
  try {
    const userRes = await store.dispatch('myMessageModule/getUser')
    const { code, data = {} } = userRes?.data || {}
    if (code !== '0') {
      ElMessage.error(userRes?.data?.msg || '用户信息加载失败')
      return
    }
    rawUserInfo.value = data
    await loadEnterpriseInfo(data.enterpriseId)
    await loadBrandOptions()
    fillPageData(data)
    await loadInviteDisplay()
  } catch (error) {
    ElMessage.error('用户信息加载失败')
  }
}

const handleCancel = () => {
  formData.value = cloneData(initialFormData.value)
}

const handleConfirm = async () => {
  const valid = await formRef.value?.validate().catch(() => false)
  if (valid === false) {
    return
  }

  const formattedBirthMonth = formData.value.birthMonth
    ? moment(formData.value.birthMonth).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss.SSSZZ')
    : ''

  const submitData = {
    ...cloneData(rawUserInfo.value),
    realName: formData.value.name,
    birthMonth: formattedBirthMonth,
    gender: formData.value.gender,
    province: formData.value.addressArea[0] || '',
    city: formData.value.addressArea[1] || '',
    district: formData.value.addressArea[2] || '',
    address: formData.value.address,
    jobPost: formData.value.job,
    brandName: formData.value.brand.join(','),
    yearOfWork: formData.value.experience
  }

  try {
    const res = await store.dispatch('myMessageModule/userEditUser', submitData)
    const { code, msg } = res?.data || {}
    if (code === '0') {
      ElMessage.success('修改成功')
      await initPage()
      return
    }
    ElMessage.error(msg || '修改失败')
  } catch (error) {
    ElMessage.error('修改失败')
  }
}

const copyLink = value => {
  if (!value) {
    ElMessage.warning('当前没有可复制的链接')
    return
  }
  copyText(value)
}

onMounted(() => {
  initPage()
})
</script>

<template>
  <div class="supplierInfo">
    <div class="welcome-card">
      <!-- 核心内容区域 -->
      <div class="welcome-content">
        <p class="welcome-title">
          尊敬的<span class="highlight-text">{{ welcomeName }}</span>，热烈欢迎!您已成为盘雷第<span class="highlight-text">{{ memberOrder }}</span
          >位重要成员。
        </p>
        <p class="welcome-paragraph">
          盘雷是一个完全免费，开放共享的数字供应链生态。您不仅可以在此便捷地进行销售与采购，更能将您手中的行业资源盘活，直接转化为生态红利。
        </p>
        <p class="welcome-paragraph">
          邀请您的上下游伙伴加入，不仅能提升协作效率，更能启动您资源的"滚雪球"效应：让客户带来客户，实现可持续的增长与收益。
        </p>
        <p class="welcome-paragraph">
          若想快速获取资源优势，也可直接开通会员，一键直达特定地区或品牌的全量供应商网络。
        </p>
        <p class="welcome-paragraph">
          我们建议您：立即开始[推广者1级]学习，精通平台工具；在达到2级后，即可申请晋升为盘雷开发者合伙人，解锁更高阶的权益，与我们共享生态的长期价值。
        </p>
      </div>
    </div>
    <div class="base-info">
      <el-tabs v-model="activeTab" class="tabs">
        <el-tab-pane label="基础信息" name="baseInfo"></el-tab-pane>
      </el-tabs>
      <div class="base-info-page">
        <el-row :gutter="24" class="info-content">
          <!-- 左侧：基础信息表单区域 -->
          <el-col :span="12">
            <div class="left-section">
              <div class="avatar-wrapper">
                <el-avatar
                  size="80"
                  src="https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png"
                  class="user-avatar"
                />
              </div>
              <div class="info-form">
                <el-form ref="formRef" :model="formData" :rules="rules" label-width="120px" label-position="left">
                  <el-form-item label="名字" prop="name">
                    <el-input v-model="formData.name" placeholder="请输入姓名" style="width: 180px" />
                  </el-form-item>
                  <el-form-item label="盘雷ID：" prop="panLeiId">
                    <span>{{ displayValue(formData.panLeiId) }}</span>
                  </el-form-item>
                  <el-form-item label="用户类型：" prop="userType">
                    <el-radio-group v-model="formData.userType" disabled>
                      <el-radio label="factory">厂家</el-radio>
                      <el-radio label="supplier">供应商</el-radio>
                      <el-radio label="customer">客户</el-radio>
                    </el-radio-group>
                    <span class="type-tip">(联系盘雷客服修改类型)</span>
                  </el-form-item>
                  <el-form-item label="手机号(登录名)：" prop="phone">
                    <span>{{ displayValue(formData.phone) }}</span>
                  </el-form-item>
                  <el-form-item label="企业名称：" prop="enterpriseName">
                    <span>{{ displayValue(formData.enterpriseName) }}</span>
                  </el-form-item>
                  <el-form-item label="企业ID：" prop="enterpriseId">
                    <span>{{ displayValue(formData.enterpriseId) }}</span>
                  </el-form-item>
                </el-form>
                <div class="form-footer">
                  <el-button @click="handleCancel">取消</el-button>
                  <el-button type="primary" @click="handleConfirm">确认</el-button>
                </div>
              </div>
            </div>
          </el-col>

          <!-- 右侧：入驻与邀请人信息区域 -->
          <el-col :span="12" class="right-col">
            <div class="settle-duration">
              您已入驻盘雷<span class="highlight">{{ settleYears }}年{{ settleMonths }}个月</span>
            </div>
            <el-divider />

            <div class="inviter-section">
              <h4 class="inviter-title">盘雷数字合作伙伴（邀请人）：</h4>
              <el-descriptions :column="2" border="false" class="inviter-desc">
                <el-descriptions-item label="名字:">{{ displayValue(inviterInfo.name) }}</el-descriptions-item>
                <el-descriptions-item label="手机号:">{{ displayValue(inviterInfo.phone) }}</el-descriptions-item>
                <el-descriptions-item label="ID:">{{ displayValue(inviterInfo.id) }}</el-descriptions-item>
                <el-descriptions-item label="企业:">{{ displayValue(inviterInfo.enterprise) }}</el-descriptions-item>
                <el-descriptions-item label="入驻时间:" :span="2">{{ displayValue(inviterInfo.settleTime) }}</el-descriptions-item>
              </el-descriptions>
            </div>

            <div class="stats-card">
              <el-row :gutter="0">
                <el-col :span="8" class="stats-item">
                  <span class="stats-label">供应商：</span>
                  <span class="stats-value">{{ displayValue(stats.supplier) }}</span>
                </el-col>
                <el-col :span="8" class="stats-item">
                  <span class="stats-label">客户：</span>
                  <span class="stats-value">{{ displayValue(stats.customer) }}</span>
                </el-col>
                <el-col :span="8" class="stats-item">
                  <span class="stats-label">数字伙伴：</span>
                  <span class="stats-value">{{ displayValue(stats.partner) }}</span>
                </el-col>
              </el-row>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="developer-page">
      <div class="developer-content">
        <!-- 顶部标题栏 -->
        <div class="page-header">
          <span class="header-title">加入盘雷开发者，成为平台合伙人，共建生态，共享增长</span>
        </div>

        <!-- 模块1：精准资源推荐 -->
        <div class="module">
          <div class="module-title">
            <div>一</div>
            <span class="title-text">根据您的资料，持续接收精准的资源。</span>
          </div>
          <p class="module-desc">
            根据您填写的详细信息，盘雷将为您智能推送不限于高价值行业机会、销售线索与采购资源的专属信息，助您持续拓展商业视野。
          </p>

          <el-form :model="formData" label-width="100px" class="info-form">
            <el-form-item label="出生年月:">
              <el-date-picker
                v-model="formData.birthMonth"
                type="date"
                placeholder="请选择"
                style="width: 200px"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
              />
            </el-form-item>
            <el-form-item label="性 别:">
              <el-radio-group v-model="formData.gender">
                <el-radio label="male">男</el-radio>
                <el-radio label="female">女</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="实际住址:">
              <el-cascader
                v-model="formData.addressArea"
                :options="areaInfo"
                :props="{ expandTrigger: 'hover' }"
                placeholder="请选择地区"
                style="width: 320px"
              />
            </el-form-item>
            <el-form-item label="详细住址:">
              <el-input
                v-model="formData.address"
                placeholder="四川省成都市武候区XXXX街道XXXX号"
                style="width: 400px"
              />
            </el-form-item>
            <el-form-item label="从事工作:">
              <el-select
                v-model="formData.job"
                placeholder="请选择"
                style="width: 200px"
                filterable
              >
                <el-option v-for="item in selectOptions.job" :key="item.key" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
            <el-form-item label="熟悉品牌:">
              <el-select
                v-model="formData.brand"
                multiple
                placeholder="请选择"
                style="width: 200px"
                filterable
                collapse-tags
                collapse-tags-tooltip
              >
                <el-option v-for="item in selectOptions.brand" :key="item.key" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
            <el-form-item label="从业年限:">
              <el-select
                v-model="formData.experience"
                placeholder="请选择"
                style="width: 200px"
                filterable
              >
                <el-option v-for="item in selectOptions.experience" :key="item.key" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button @click="handleCancel">取消</el-button>
              <el-button type="primary" @click="handleConfirm" class="confirm-btn">确认</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 模块2：成长路径与权益 -->
        <div class="module">
          <div class="module-title">
            <div>二</div>
            <span class="title-text">未来已来，你在哪里？盘雷路径：推广者 → 开发者 → 资源放大10倍</span>
          </div>
          <div class="current-level">
            <span class="level-label">您当前为：</span>
            <el-tag type="success" class="level-tag">{{ devLevelText }}</el-tag>
            <span class="level-tip">（请尽快使用APP学习）</span>
            <span class="level-tip">请使用小程序/APP学习技巧，晋升至1级。</span>
          </div>

          <div class="benefits-card">
            <h4 class="benefits-title">盘雷推广者核心权益与规则说明：</h4>
            <ul class="benefits-list">
              <li><strong>推广者2级：采购佣金</strong><br />您邀请的供应商通过平台采购，您最高可获得50%交易佣金。</li>
              <li><strong>推广者3级：会员分成</strong><br />您邀请的供应商购买任何盘雷会员，您均可获得50%销售分成。</li>
              <li>
                <strong>推广者4级：盘雷终身会员</strong
                ><br />盘雷终身会员，共享盘雷全平台供应链资源。所有品牌与价格对您开放查询，强力赋能您的销售业务
              </li>
            </ul>
            <h4 class="benefits-title">重要规则提示：</h4>
            <ul class="rules-list">
              <li>统计标准：推广者等级仅统计由您邀请并新注册的供应商。</li>
              <li>邀请与绑定：已注册用户无法再被邀请，但可添加为“绑定供应商”，建立合作联系。</li>
              <li>唯一性原则：任何供应商仅能成为一位推广者的“邀请供应商”。</li>
              <li>未来已来，尽快行动，使用盘雷工业数字链，抢占市场稀缺资源！</li>
            </ul>
          </div>
        </div>

        <!-- 模块3：邀请链接 -->
        <div class="module">
          <div class="module-title">
            <div>三</div>
            <span class="title-text">网页邀请链接（推荐使用小程序链接邀请）</span>
          </div>
          <el-row :gutter="40" class="invite-content">
            <!-- 左侧：供应商邀请 -->
            <el-col :span="12" class="invite-col">
              <div class="invite-desc">
                <p>
                  诚邀伙伴，共享增长。请将此链接发给你合作的各类供应商(厂家/代理/贸易商/门店)。他注册，你受益：不仅助力你晋升推广者等级，他们设置的品牌与产品信息更能直接为你所用，让你快速分类供应商，高效完成采购。
                </p>
              </div>
              <div class="link-area">
                <div class="link-label">供应商邀请地址：</div>
                <div class="link-text">{{ displayValue(supplierLink) }}</div>
                <el-button type="text" class="copy-btn" @click="copyLink(supplierLink)">复制</el-button>
              </div>
            </el-col>

            <!-- 右侧：客户邀请 -->
            <el-col :span="12" class="invite-col">
              <div class="invite-desc">
                <p>邀您的客户入驻盘雷，不仅是为其启高效协作的通道，更是为您的事业装上一台"资源引擎"。</p>
                <p>
                  请将您的专属注册链接发送给服务的各类采购型客户，如最终用户，设备制造商，盘柜厂及系统集成商等。他们通过此链接入驻后，即成为您的专属服务客户。您可使用盘雷的"一站式"线上工具，为他们提供从快速报价，自动生成合同到跟踪发货的全流程支持，极大提升服务效率与专业体验。
                </p>
                <p>
                  更大的价值在于网络裂变。成功邀请客户后，您将自动升级为"盘雷数字合作伙伴"。此后，您的每一位专属客户在盘雷上邀请的任何新注册供应商或采购伙伴，都将通过数字链规则自动关联至您的网络。这意味着，您的客户资源将像网络一样自主扩展，实现"客户带来新客户"的几何级增长，将一次性的服务动作转化为持续的资源收益。
                </p>
                <p>这不仅是在服务客户，更是在构建属于于您自己的，不断生长的数字化事业生态。</p>
              </div>
              <div class="link-area">
                <div class="link-label">客户邀请地址：</div>
                <div class="link-text">{{ displayValue(customerLink) }}</div>
                <el-button type="text" class="copy-btn" @click="copyLink(customerLink)">复制</el-button>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.supplierInfo {
  width: 1400px;
  height: 100%;
  margin: 0 auto;
  .welcome-card {
    width: 100%;
    background: #ffffff;

    height: 190px;
    border-radius: 8px;
    .welcome-content {
      padding: 25px;
      box-sizing: border-box;
      .welcome-title {
        font-size: 14px;
        font-weight: 400;
        margin: 0 0 8px 0;
      }
      .welcome-paragraph {
        font-size: 14px;
        line-height: 8px;
        color: #333333;
        margin: 0 0 28px 0;
        text-indent: 2em; /* 首行缩进，匹配设计图排版 */
      }
    }
  }
  .base-info {
    margin-top: 24px;
    background: #ffffff;
    .tabs {
      padding: 20px;
      :deep(.el-tabs__item) {
        padding: 0 20px;
        height: 40px;
        line-height: 40px;
        font-size: 14px;
        color: #606666;
        border-bottom: 3px solid transparent;
        transition: all 0.3s ease;
        &:hover {
          columns: #48a09c;
        }
        &.is-active {
          color: #48a09c;
          border-bottom: 3px solid #48a09c;
          font-weight: 500;
        }
      }
      :deep(.el-tabs__header) {
        background: #ffffff !important;
        border-bottom: 1px solid #e5e5e5;
      }

      :deep(.el-tabs__header) {
        border-bottom: none;
      }
      :deep(.el-tabs__active-bar) {
        height: 0;
      }
    }

    .base-info-page {
      padding: 0 24px;
      background: #ffffff;
      border-radius: 8px;
      .left-section {
        display: flex;
        :deep(.el-form-item) {
          margin-bottom: 0;
        }
      }
    }

    .left-col {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .avatar-wrapper {
      width: 120px;
      display: flex;
      justify-content: flex-start;
    }

    .user-avatar {
      background-color: #36cbcb;
    }

    .info-form {
      width: 100%;
      .form-footer {
        margin-top: 70px;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
      }
    }

    .type-tip {
      margin-top: 8px;
      font-size: 14px;
      color: #606266;
    }

    .confirm-btn {
      background-color: #369a9a;
      border-color: #369a9a;
    }

    .confirm-btn:hover {
      background-color: #2d8585;
      border-color: #2d8585;
    }

    .right-col {
      display: flex;
      flex-direction: column;
      gap: 24px;

      border-left: 1px solid #e4e7ed;
    }

    .settle-duration {
      font-size: 20px;
      color: #303133;
    }

    .highlight {
      color: #e53e3e;
      font-weight: 500;
    }

    .inviter-title {
      font-size: 16px;
      color: #303133;
      margin-bottom: 16px;
    }

    .inviter-desc {
      --el-descriptions-label-width: 80px;
    }

    .inviter-desc :deep(.el-descriptions__label) {
      color: #606266;
    }

    .inviter-desc :deep(.el-descriptions__content) {
      color: #303133;
    }

    .stats-card {
      background-color: #f5f7fa;
      padding: 24px;
      border-radius: 4px;
    }

    .stats-item {
      text-align: center;
      font-size: 16px;
    }

    .stats-label {
      color: #606266;
    }

    .stats-value {
      color: #303133;
      font-weight: 500;
      margin-left: 8px;
    }
  }
  .developer-page {
    width: 100%;
    margin-top: 24px;
    background: #ffffff;
    border-radius: 8px;
    .developer-content {
      width: 1360px;
      margin: 20px;
      border: 1px solid #5bc4bf;
      border-radius: 8px;
    }

    .page-header {
      background-color: #369a9a;
      color: #ffffff;
      padding: 8px 16px;
      border-radius: 4px;
      margin-bottom: 24px;
      text-align: center;
    }
    .header-title {
      font-size: 16px;
      font-weight: 500;
    }

    .module {
      margin: 24px;
    }
    .module-title {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      div {
        width: 22px;
        height: 22px;
        line-height: 22px;
        text-align: center;
        background: #48a09c;
        color: #ffffff;
        font-size: 14px;
        border-radius: 2px;
      }
    }

    .title-text {
      margin-left: 8px;
      font-size: 16px;
      font-weight: 500;
      color: #48a09c;
    }
    .module-desc {
      font-size: 14px;
      color: #606266;
      margin-bottom: 16px;
    }

    .info-form {
      width: 100%;
    }
    .confirm-btn {
      background-color: #369a9a;
      border-color: #369a9a;
    }
    .confirm-btn:hover {
      background-color: #2d8585;
      border-color: #2d8585;
    }

    .current-level {
      margin-bottom: 16px;
    }
    .level-label {
      font-size: 14px;
      color: #303133;
      margin-right: 8px;
    }
    .level-tag {
      margin-right: 8px;
    }
    .level-tip {
      font-size: 12px;
      color: #606266;
    }

    .benefits-card {
      background-color: #f5f7fa;
      padding: 16px;
      border-radius: 4px;
    }
    .benefits-title {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 8px;
    }
    .benefits-list,
    .rules-list {
      margin: 0;
      padding-left: 20px;
      font-size: 14px;
      color: #303133;
    }
    .benefits-list li,
    .rules-list li {
      margin-bottom: 8px;
    }

    .invite-content {
      position: relative;
    }
    .invite-content::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      width: 1px;
      height: 100%;
      background: #e4e7ed;
      transform: translateX(-50%);
    }
    .invite-col {
      padding: 0 20px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 100%;
    }

    .invite-desc {
      font-size: 16px;
      color: #303133;
      line-height: 1.8;
      margin-bottom: 32px;
      flex-grow: 1;
    }
    .invite-desc p {
      margin: 0 0 12px 0;
      text-indent: 2em;
    }
    .invite-desc p:last-child {
      margin-bottom: 0;
    }

    .link-area {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: auto;
    }
    .link-label {
      font-size: 14px;
      color: #909399;
    }
    .link-text {
      font-size: 16px;
      color: #303133;
      word-break: break-all;
    }
    .copy-btn {
      align-self: flex-start;
      color: #369a9a;
      font-size: 16px;
      padding: 0;
    }
    .copy-btn:hover {
      color: #2d8585;
    }
  }
}
</style>
